import { createContext, useContext, useEffect, useMemo, useState, ReactNode, createElement } from 'react';
import { useLocation } from 'react-router-dom';

export const DEFAULT_DOCUMENT_TITLE = 'ORBCoN - Ontario Regional Blood Coordinating Network';
export const SITE_NAME = 'ORBCoN';

const PageTitleContext = createContext<(title: string | null) => void>(() => {});

function formatPageTitle(page?: string | null): string {
  const trimmed = page?.trim();
  return trimmed ? `${trimmed} | ${SITE_NAME}` : DEFAULT_DOCUMENT_TITLE;
}

function staticTitleForPath(pathname: string, search: string): string | null {
  if (pathname === '/') return null;

  const exact: Record<string, string> = {
    '/resources': 'Resources',
    '/library': 'Resource library',
    '/events': 'Events',
    '/report': 'The ORBCoN Report',
    '/dashboard': 'Dashboard',
    '/profile': 'Profile',
    '/data': 'Data',
    '/elearning': 'eLearning',
    '/elearning/certifications': 'Certificates',
    '/about': 'About',
    '/admin/hubs': 'Manage toolkits',
    '/admin/hubs/new': 'New toolkit',
    '/admin/programs': 'Programs and references',
    '/admin/programs/new': 'New program',
    '/admin/hospital-domains': 'Hospital domains',
    '/elearning/admin/courses': 'eLearning courses',
    '/elearning/admin/courses/new': 'New Course',
  };

  if (exact[pathname]) return exact[pathname];

  if (pathname === '/search') {
    const query = new URLSearchParams(search).get('q') || new URLSearchParams(search).get('search');
    return query?.trim() ? `Search: ${query.trim()}` : 'Search';
  }

  if (pathname.startsWith('/elearning/admin/courses/') && pathname.endsWith('/modules/new')) {
    return 'New Module';
  }

  return null;
}

export function PageTitleProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [override, setOverride] = useState<{ path: string; title: string } | null>(null);

  const setPageTitle = useMemo(() => {
    return (title: string | null) => {
      const trimmed = title?.trim() ?? '';
      setOverride(trimmed ? { path: location.pathname, title: trimmed } : null);
    };
  }, [location.pathname]);

  const resolved = useMemo(() => {
    const active = override?.path === location.pathname ? override.title : null;
    if (active) return formatPageTitle(active);
    const fromRoute = staticTitleForPath(location.pathname, location.search);
    return formatPageTitle(fromRoute);
  }, [override, location.pathname, location.search]);

  useEffect(() => {
    document.title = resolved;
  }, [resolved]);

  return createElement(PageTitleContext.Provider, { value: setPageTitle }, children);
}

/** Set a document title for the current screen (detail pages). Pass null to use the route default. */
export function usePageTitle(title?: string | null) {
  const setPageTitle = useContext(PageTitleContext);

  useEffect(() => {
    setPageTitle(title?.trim() ? title.trim() : null);
  }, [title, setPageTitle]);
}
